import { PublicClientApplication, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for Azure AD authentication
 * Requires environment variables:
 * - VITE_AZURE_AD_CLIENT_ID: App registration client ID
 * - VITE_AZURE_AD_TENANT_ID: Azure AD tenant ID
 * - VITE_AZURE_AD_REDIRECT_URI: Redirect URI (optional, defaults to window.location.origin)
 *
 * IMPORTANT: Azure AD App Registration Setup
 * 1. Go to Azure Portal > Azure Active Directory > App registrations
 * 2. Select your app (or create new)
 * 3. Go to Authentication > Platform configurations
 * 4. Add a platform: "Single-page application" (SPA)
 *    - DO NOT use "Web" platform - this causes AADSTS9002326 errors
 * 5. Set redirect URI to http://localhost:3000 (or your app URL)
 * 6. Under "Implicit grant and hybrid flows", leave both checkboxes UNCHECKED
 *    (MSAL.js uses authorization code flow with PKCE)
 */
export const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID || 'common'}`,
    redirectUri: import.meta.env.VITE_AZURE_AD_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii && import.meta.env.DEV) {
          switch (level) {
            case LogLevel.Error:
              console.error(`[MSAL] ${message}`);
              break;
            case LogLevel.Warning:
              console.warn(`[MSAL] ${message}`);
              break;
            case LogLevel.Info:
              console.info(`[MSAL] ${message}`);
              break;
            default:
              break;
          }
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

/**
 * Scopes required for Microsoft Graph Mail API
 */
export const graphScopes = {
  scopes: ['Mail.ReadWrite', 'User.Read'],
};

/**
 * Login request configuration for basic authentication
 */
export const loginRequest = {
  scopes: ['User.Read'],
};

/**
 * Scopes for Graph API features (like creating drafts)
 */
export const graphMailScopes = {
  scopes: ['Mail.ReadWrite', 'User.Read'],
};

/**
 * Create and export the MSAL instance
 * Only initialize if client ID is configured
 */
let msalInstance = null;

export function getMsalInstance() {
  if (!msalInstance && import.meta.env.VITE_AZURE_AD_CLIENT_ID) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

/**
 * Check if Azure AD is configured
 */
export function isAzureAdConfigured() {
  return Boolean(import.meta.env.VITE_AZURE_AD_CLIENT_ID);
}
